<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useSubjectsStore } from '@/stores/subjects'
import FosInput from '@/components/ui/FosInput.vue'
import FosTextarea from '@/components/ui/FosTextarea.vue'
import FosSelect from '@/components/ui/FosSelect.vue'

const props = withDefaults(
  defineProps<{
    subjectId: string
    field: string
    label: string
    type: 'text' | 'tel' | 'textarea' | 'select' | 'date' | 'time'
    options?: string[]
    alwaysEditable?: boolean
    readonly?: boolean
  }>(),
  {
    alwaysEditable: false,
    readonly: false,
  },
)

const route = useRoute()
const incidentId = route.params.id as string
const subjectsStore = useSubjectsStore()

const editing = ref(false)
const saved = ref(false)
let savedTimer: ReturnType<typeof setTimeout> | null = null

const currentValue = computed(() => {
  const subject = subjectsStore.activeSubject
  if (!subject) return null
  return (subject as unknown as Record<string, unknown>)[props.field] ?? null
})

const displayValue = computed(() => {
  const v = currentValue.value
  if (v === null || v === undefined || v === '') return null
  return String(v)
})

const inputValue = ref<string>('')

function startEditing() {
  if (props.readonly) return
  inputValue.value = displayValue.value ?? ''
  editing.value = true
}

async function commitEdit(newVal: string) {
  editing.value = false
  const trimmed = newVal.trim()
  const prev = displayValue.value ?? ''
  if (trimmed === prev) return

  await subjectsStore.updateSubjectField(incidentId, props.subjectId, props.field, trimmed || null)

  saved.value = true
  if (savedTimer) clearTimeout(savedTimer)
  savedTimer = setTimeout(() => {
    saved.value = false
  }, 2000)
}

function onBlur(e: Event) {
  const val = (e.target as HTMLInputElement | HTMLTextAreaElement).value
  commitEdit(val)
}

function onSelectChange(val: string) {
  commitEdit(val)
}
</script>

<template>
  <div class="flex flex-col gap-0.5">
    <label class="text-[10px] text-outline font-label uppercase tracking-widest">{{ label }}</label>

    <!-- Readonly mode -->
    <template v-if="readonly">
      <span class="text-on-surface-variant text-sm font-mono">{{ displayValue ?? '—' }}</span>
    </template>

    <!-- Select — always shown as select -->
    <template v-else-if="type === 'select'">
      <FosSelect
        :model-value="displayValue ?? ''"
        :options="options ?? []"
        placeholder="—"
        @update:model-value="onSelectChange"
      />
    </template>

    <!-- Text / tel / date / time -->
    <template v-else-if="type === 'text' || type === 'tel' || type === 'date' || type === 'time'">
      <template v-if="alwaysEditable || editing">
        <FosInput
          :model-value="alwaysEditable ? (displayValue ?? '') : inputValue"
          :type="type"
          @blur="onBlur"
          @update:model-value="(v) => { if (alwaysEditable) inputValue = v as string }"
        />
      </template>
      <template v-else>
        <button
          type="button"
          class="text-left text-sm group flex items-center gap-1"
          @click="startEditing"
        >
          <span :class="displayValue ? 'text-on-surface font-mono' : 'text-outline font-mono'">
            {{ displayValue ?? '—' }}
          </span>
          <span class="material-symbols-outlined text-[14px] text-outline opacity-0 group-hover:opacity-100 transition-opacity">edit</span>
        </button>
      </template>
    </template>

    <!-- Textarea -->
    <template v-else-if="type === 'textarea'">
      <template v-if="alwaysEditable || editing">
        <FosTextarea
          :model-value="alwaysEditable ? (displayValue ?? '') : inputValue"
          :rows="3"
          @blur="onBlur"
          @update:model-value="(v) => { if (alwaysEditable) inputValue = v as string }"
        />
      </template>
      <template v-else>
        <button
          type="button"
          class="text-left text-sm group flex items-start gap-1"
          @click="startEditing"
        >
          <span :class="displayValue ? 'text-on-surface font-mono whitespace-pre-wrap' : 'text-outline font-mono'">
            {{ displayValue ?? '—' }}
          </span>
          <span class="material-symbols-outlined text-[14px] text-outline opacity-0 group-hover:opacity-100 transition-opacity mt-0.5">edit</span>
        </button>
      </template>
    </template>

    <!-- Saved flash -->
    <transition
      enter-active-class="transition-opacity duration-150"
      leave-active-class="transition-opacity duration-300"
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
    >
      <span v-if="saved" class="text-[10px] text-primary font-mono uppercase tracking-widest">Saved</span>
    </transition>
  </div>
</template>
